import { useCallback, useMemo, useState, type DragEvent as ReactDragEvent } from 'react';
import GridLayout, { type Layout } from 'react-grid-layout';
import { WidthProvider } from 'react-grid-layout';
import { nanoid } from '@reduxjs/toolkit';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleIcon from '@mui/icons-material/AddCircle';

import {
  CanvasMode,
  type Widget,
  type WidgetDefinition,
  type WidgetConfig,
  type WidgetState,
  type WidgetInput,
  type WidgetOutput,
} from '../canvas/types/index.js';
import { widgetRegistry } from '../canvas/utils/widgetRegistry.js';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ReactGridLayout = WidthProvider(GridLayout);

const GRID_COLUMNS = 12;
const GRID_COLUMN_WIDTH = 120;
const GRID_ROW_HEIGHT = 120;

const toGridWidth = (definition: WidgetDefinition) => {
  const base = definition.defaultSize?.width ?? 360;
  return Math.min(
    GRID_COLUMNS,
    Math.max(2, Math.round(base / GRID_COLUMN_WIDTH))
  );
};

const toGridHeight = (definition: WidgetDefinition) => {
  const base = definition.defaultSize?.height ?? 240;
  return Math.max(2, Math.round(base / GRID_ROW_HEIGHT));
};

const toPxSize = (layoutItem: Layout) => ({
  width: layoutItem.w * GRID_COLUMN_WIDTH,
  height: layoutItem.h * GRID_ROW_HEIGHT,
});

type EditorWidgetInstance = {
  id: string;
  definition: WidgetDefinition;
  widget: Widget;
};

const createWidgetInstance = (
  definition: WidgetDefinition,
  layoutItem: Layout
): EditorWidgetInstance => {
  const id = nanoid();
  const config: WidgetConfig = {
    ...definition.defaultConfig,
  };

  const inputs: WidgetInput[] = definition.inputDefinitions.map((inputDef) => ({
    id: inputDef.id,
    name: inputDef.name,
    type: inputDef.type,
    schema: inputDef.schema,
    acceptsMultiple: inputDef.acceptsMultiple,
    value: inputDef.defaultValue,
  }));

  const outputs: WidgetOutput[] = definition.outputDefinitions.map((outputDef) => ({
    id: outputDef.id,
    name: outputDef.name,
    type: outputDef.type,
    schema: outputDef.schema,
    value: null,
  }));

  const pxSize = toPxSize(layoutItem);

  const widget: Widget = {
    id,
    type: definition.type,
    position: {
      x: layoutItem.x * GRID_COLUMN_WIDTH,
      y: layoutItem.y * GRID_ROW_HEIGHT,
    },
    size: {
      width: pxSize.width,
      height: pxSize.height,
    },
    zIndex: Date.now(),
    config,
    inputs,
    outputs,
    state: {
      loading: false,
    },
  };

  return {
    id,
    definition,
    widget,
  };
};

const mergeWidgetConfig = (widget: Widget, config: Partial<WidgetConfig>): Widget => ({
  ...widget,
  config: {
    ...widget.config,
    ...config,
  },
});

const mergeWidgetOutputs = (widget: Widget, outputs: Partial<Record<string, any>>): Widget => ({
  ...widget,
  outputs: widget.outputs.map((output) =>
    Object.prototype.hasOwnProperty.call(outputs, output.id)
      ? { ...output, value: outputs[output.id] }
      : output
  ),
});

const mergeWidgetState = (widget: Widget, state: Partial<WidgetState>): Widget => ({
  ...widget,
  state: {
    ...widget.state,
    ...state,
  },
});

const mergeWidgetInputs = (widget: Widget, inputs: Partial<Record<string, any>>): Widget => ({
  ...widget,
  inputs: widget.inputs.map((input) =>
    Object.prototype.hasOwnProperty.call(inputs, input.id)
      ? { ...input, value: inputs[input.id] }
      : input
  ),
});

const updateWidgetSizeAndPosition = (widget: Widget, layoutItem: Layout): Widget => {
  const pxSize = toPxSize(layoutItem);
  return {
    ...widget,
    position: {
      x: layoutItem.x * GRID_COLUMN_WIDTH,
      y: layoutItem.y * GRID_ROW_HEIGHT,
    },
    size: {
      width: pxSize.width,
      height: pxSize.height,
    },
  };
};

const paletteCategories: ReadonlyArray<WidgetDefinition['category']> = [
  'market',
  'analysis',
  'portfolio',
  'profile',
  'data',
];

export const DashboardEditorPage: React.FC = () => {
  const theme = useTheme();
  const [layout, setLayout] = useState<Layout[]>([]);
  const [widgets, setWidgets] = useState<EditorWidgetInstance[]>([]);
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [droppingPreview, setDroppingPreview] = useState<{ w: number; h: number } | null>(null);

  const widgetDefinitions = useMemo(() => {
    return widgetRegistry
      .list()
      .filter((definition) => paletteCategories.includes(definition.category))
      .filter((definition) => definition.type !== 'widgetGroup');
  }, []);

  const definitionsByCategory = useMemo(() => {
    return widgetDefinitions.reduce(
      (acc, definition) => {
        const group = acc.get(definition.category) ?? [];
        group.push(definition);
        acc.set(definition.category, group);
        return acc;
      },
      new Map<WidgetDefinition['category'], WidgetDefinition[]>()
    );
  }, [widgetDefinitions]);

  const addWidgetFromDefinition = useCallback(
    (definition: WidgetDefinition, position?: Partial<Layout>) => {
      const w = toGridWidth(definition);
      const h = toGridHeight(definition);
      const baseLayout: Layout = {
        i: 'temp',
        x: position?.x ?? 0,
        y: position?.y ?? Infinity,
        w: position?.w ?? w,
        h: position?.h ?? h,
        minW: Math.max(2, Math.min(w, GRID_COLUMNS)),
        minH: Math.max(2, h),
      };
      const instance = createWidgetInstance(definition, baseLayout);
      setWidgets((prev) => [...prev, instance]);
      setLayout((prev) => [
        ...prev,
        {
          ...baseLayout,
          i: instance.id,
        },
      ]);
      setSelectedWidgetId(instance.id);
    },
    []
  );

  const handleLayoutChange = useCallback(
    (nextLayout: Layout[]) => {
      setLayout(nextLayout);
      setWidgets((prev) =>
        prev.map((instance) => {
          const entry = nextLayout.find((item) => item.i === instance.id);
          if (!entry) return instance;
          return {
            ...instance,
            widget: updateWidgetSizeAndPosition(instance.widget, entry),
          };
        })
      );
    },
    []
  );

  const handleRemoveWidget = useCallback((id: string) => {
    setWidgets((prev) => prev.filter((instance) => instance.id !== id));
    setLayout((prev) => prev.filter((item) => item.i !== id));
    setSelectedWidgetId((current) => (current === id ? null : current));
  }, []);

  const handlePaletteClick = useCallback(
    (definition: WidgetDefinition) => () => {
      addWidgetFromDefinition(definition);
    },
    [addWidgetFromDefinition]
  );

  const handlePaletteDragStart = useCallback((definition: WidgetDefinition) => (event: ReactDragEvent) => {
    event.dataTransfer.setData('application/widget-type', definition.type);
    event.dataTransfer.effectAllowed = 'copy';
    setDroppingPreview({ w: toGridWidth(definition), h: toGridHeight(definition) });
  }, []);

  const handlePaletteDragEnd = useCallback(() => {
    setDroppingPreview(null);
  }, []);

  const handleDrop = useCallback(
    (_layout: Layout[], layoutItem: Layout, event: MouseEvent) => {
      const dataTransfer = (event as DragEvent | null)?.dataTransfer;
      const type = dataTransfer?.getData('application/widget-type');
      if (!type) return;
      const definition = widgetDefinitions.find((def) => def.type === type);
      if (!definition) return;
      addWidgetFromDefinition(definition, layoutItem);
      setDroppingPreview(null);
    },
    [addWidgetFromDefinition, widgetDefinitions]
  );

  const renderWidgetInstance = useCallback(
    (instance: EditorWidgetInstance) => {
      const fallbackWidth = toGridWidth(instance.definition);
      const fallbackHeight = toGridHeight(instance.definition);
      const layoutItem = layout.find((item) => item.i === instance.id) ?? {
        i: instance.id,
        x: 0,
        y: Infinity,
        w: fallbackWidth,
        h: fallbackHeight,
        minW: Math.max(2, Math.min(fallbackWidth, GRID_COLUMNS)),
        minH: Math.max(2, fallbackHeight),
      };
      const WidgetComponent = instance.definition.component;
      return (
        <Box
          key={instance.id}
          data-grid={layoutItem}
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            border: selectedWidgetId === instance.id
              ? `2px solid ${theme.palette.primary.main}`
              : `1px solid ${theme.palette.divider}`,
            boxShadow: selectedWidgetId === instance.id ? theme.shadows[6] : theme.shadows[2],
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={() => setSelectedWidgetId(instance.id)}
        >
          <Stack
            className="dashboard-editor-widget-header"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 1.5,
              py: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: selectedWidgetId === instance.id
                ? theme.palette.action.selected
                : theme.palette.background.default,
              cursor: 'move',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {instance.definition.icon && (
                <Box component={instance.definition.icon} sx={{ fontSize: 20 }} />
              )}
              <Typography variant="subtitle2" noWrap>
                {instance.definition.name}
              </Typography>
            </Stack>
            <Tooltip title="Remove widget">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleRemoveWidget(instance.id);
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Box sx={{ flex: 1, minHeight: 120, overflow: 'hidden' }}>
            <WidgetComponent
              widget={instance.widget}
              onUpdateConfig={(config) =>
                setWidgets((prev) =>
                  prev.map((item) =>
                    item.id === instance.id
                      ? {
                          ...item,
                          widget: mergeWidgetConfig(item.widget, config),
                        }
                      : item
                  )
                )
              }
              onUpdateOutputs={(outputs) =>
                setWidgets((prev) =>
                  prev.map((item) =>
                    item.id === instance.id
                      ? {
                          ...item,
                          widget: mergeWidgetOutputs(item.widget, outputs),
                        }
                      : item
                  )
                )
              }
              onUpdateState={(state) =>
                setWidgets((prev) =>
                  prev.map((item) =>
                    item.id === instance.id
                      ? {
                          ...item,
                          widget: mergeWidgetState(item.widget, state),
                        }
                      : item
                  )
                )
              }
              onUpdateInputs={(inputs) =>
                setWidgets((prev) =>
                  prev.map((item) =>
                    item.id === instance.id
                      ? {
                          ...item,
                          widget: mergeWidgetInputs(item.widget, inputs ?? {}),
                        }
                      : item
                  )
                )
              }
              isSelected={selectedWidgetId === instance.id}
              canvasMode={CanvasMode.DESKTOP}
            />
          </Box>
        </Box>
      );
    },
    [handleRemoveWidget, layout, selectedWidgetId, theme.palette.action.selected, theme.palette.background.default, theme.palette.background.paper, theme.palette.divider, theme.palette.primary.main]
  );

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      <Box
        sx={{
          width: 320,
          borderRight: `1px solid ${theme.palette.divider}`,
          overflowY: 'auto',
          py: 2,
          px: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography variant="h6" gutterBottom>
          Widget Library
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Drag widgets into the canvas or click to add them. Use the grid to rearrange and resize.
        </Typography>
        {[...definitionsByCategory.entries()].map(([category, definitions]) => (
          <Box key={category} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'uppercase' }} color="text.secondary">
              {category}
            </Typography>
            <List dense disablePadding>
              {definitions.map((definition) => (
                <ListItem
                  key={definition.type}
                  disableGutters
                  sx={{
                    mb: 0.5,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.default,
                    px: 1,
                    py: 0.5,
                    cursor: 'grab',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                  draggable
                  onDragStart={handlePaletteDragStart(definition)}
                  onDragEnd={handlePaletteDragEnd}
                  onClick={handlePaletteClick(definition)}
                >
                  <ListItemAvatar sx={{ minWidth: 32 }}>
                    <Box component={definition.icon} sx={{ fontSize: 20 }} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={definition.name}
                    secondary={definition.description}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Tooltip title="Add to canvas">
                    <IconButton size="small" onClick={(event) => {
                      event.stopPropagation();
                      addWidgetFromDefinition(definition);
                    }}>
                      <AddCircleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </Box>
      <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
        <Typography variant="h5" gutterBottom>
          Dashboard Editor
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ minHeight: '80vh' }}>
          <ReactGridLayout
            layout={layout}
            cols={GRID_COLUMNS}
            rowHeight={GRID_ROW_HEIGHT}
            margin={[16, 16]}
            isDroppable
            droppingItem={droppingPreview ? { i: '__dropping__', ...droppingPreview } : undefined}
            onLayoutChange={handleLayoutChange}
            onDrop={handleDrop}
            isResizable
            draggableHandle=".dashboard-editor-widget-header"
            compactType={null}
          >
            {widgets.map(renderWidgetInstance)}
          </ReactGridLayout>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardEditorPage;
